import {
  Box,
  Typography,
  Grid,
  Paper,
  Tooltip,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  IconButton,
  Collapse,
} from "@mui/material";
import { useEffect, useState } from "react";
import { ArrowLeftIcon, ArrowRightIcon } from "../../icons/index.tsx";

export default function InspStatTopTable({
  unitCount,
  reviewNgUnitCount,
  reviewNgDieCount,
  totalDieCount,
  referenceAI,
  setReferenceAI,
  referenceAVI,
  setReferenceAVI,
  rateData,
  site_option,
  siteData,
}) {
  const [isAIOpen, setIsAIOpen] = useState(false);
  const [isAVIOpen, setIsAVIOpen] = useState(false);
  const [aggregatedData, setAggregatedData] = useState({
    totalCount: 0,
    ai_missing_m: 0,
    ai_missing_s: 0,
    avi_missing_m: 0,
    avi_missing_s: 0,
    ai_over_m: 0,
    ai_over_s: 0,
    avi_over_m: 0,
    avi_over_s: 0,
  });
  const [trueCount, setTrueCount] = useState(0);

  useEffect(() => {
    const keys = ["ai", "avi", "total"];
    const trueCount = keys.filter(
      (key) => siteData.statistics[key] === true
    ).length;
    if (trueCount > 0) {
      setTrueCount(trueCount);
    }
  }, [siteData]);

  useEffect(() => {
    if (rateData && rateData.length > 0) {
      const sumData = rateData.reduce(
        (acc, curr) => {
          return {
            totalCount: acc.totalCount + Number(curr.totalCount),
            ai_missing_m: acc.ai_missing_m + Number(curr.ai_missing_m),
            ai_missing_s: acc.ai_missing_s + Number(curr.ai_missing_s),
            avi_missing_m: acc.avi_missing_m + Number(curr.avi_missing_m),
            avi_missing_s: acc.avi_missing_s + Number(curr.avi_missing_s),
            ai_over_m: acc.ai_over_m + Number(curr.ai_over_m),
            ai_over_s: acc.ai_over_s + Number(curr.ai_over_s),
            avi_over_m: acc.avi_over_m + Number(curr.avi_over_m),
            avi_over_s: acc.avi_over_s + Number(curr.avi_over_s),
          };
        },
        {
          totalCount: 0,
          ai_missing_m: 0,
          ai_missing_s: 0,
          avi_missing_m: 0,
          avi_missing_s: 0,
          ai_over_m: 0,
          ai_over_s: 0,
          avi_over_m: 0,
          avi_over_s: 0,
        }
      );
      setAggregatedData(sumData);
    }
  }, [rateData]);

  // 소숫점 두자리까지 보여주기
  const formatNumber = (number) => {
    if (!isFinite(number)) {
      return 0; // Infinity, NaN 방지
    }
    if (number === 100) {
      return 100;
    }
    return number.toFixed(2);
  };

  // 세자리마다 콤마 찍기
  const formatNumberWithComma = (number) => {
    if (!number) return 0;
    return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  const totalContent = (
    <Box
      sx={{
        mt: 3,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Typography
        variant="h4"
        sx={{ fontWeight: "bold", color: "var(--stat-text-color)" }}
      >
        {siteData.statistics.avi
          ? formatNumberWithComma(reviewNgUnitCount ?? 0)
          : formatNumberWithComma(aggregatedData.ai_over_s)}
        <Typography
          variant="caption"
          sx={{ fontSize: "15px", color: "var(--text-color)" }}
        >
          (NG)
        </Typography>{" "}
        /{" "}
        {siteData.statistics.avi
          ? formatNumberWithComma(unitCount ?? 0)
          : formatNumberWithComma(aggregatedData.totalCount)}
        <Typography
          variant="caption"
          sx={{ fontSize: "15px", color: "var(--text-color)" }}
        >
          {siteData.statistics.avi ? "(Unit)" : "(Total)"}
        </Typography>
      </Typography>
    </Box>
  );

  return (
    <Box
      sx={{ mb: 3, display: "flex", justifyContent: "space-between", gap: 2 }}
    >
      <Paper
        sx={{
          p: 2,
          backgroundColor: "var(--paper-bg-color)",
          height: "150px",
          minWidth: "300px",
          width: "100%",
        }}
      >
        <Typography
          variant="subtitle1"
          color="text.secondary"
          gutterBottom
          sx={{
            fontWeight: "bold",
            textAlign: trueCount <= 2 ? "center" : "",
            fontSize: "1.2rem",
            color: "var(--text-color)",
          }}
        >
          Total NG
        </Typography>
        {site_option !== "FUREX" ? (
          <Tooltip
            title={
              <div>
                <div>
                  총 NG 이미지 수: {formatNumberWithComma(reviewNgDieCount)}장
                </div>
                <div>
                  총 Die 이미지 수: {formatNumberWithComma(totalDieCount)}장
                </div>
              </div>
            }
            placement="top"
            arrow
            componentsProps={{
              tooltip: {
                sx: {
                  fontSize: "1rem",
                  padding: "8px 12px",
                },
              },
            }}
          >
            {totalContent}
          </Tooltip>
        ) : (
          totalContent
        )}
      </Paper>
      <Paper
        sx={{
          p: 2,
          backgroundColor: "var(--paper-bg-color)",
          height: "150px",
          minWidth: "300px",
          width: "100%",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            height: "42px",
          }}
        >
          <Typography
            variant="subtitle1"
            color="text.secondary"
            gutterBottom
            sx={{
              fontWeight: "bold",
              width: isAIOpen ? "" : "100%",
              textAlign: trueCount <= 2 ? "center" : "",
              fontSize: "1.2rem",
              color: "var(--text-color)",
            }}
          >
            AI
          </Typography>
          {siteData && siteData.statistics.avi && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                height: "42px",
              }}
            >
              <Collapse in={isAIOpen} orientation="horizontal">
                <FormControl>
                  <RadioGroup
                    row
                    value={referenceAI}
                    onChange={(e) => setReferenceAI(e.target.value)}
                  >
                    <FormControlLabel
                      value="avi"
                      control={<Radio sx={{ color: "var(--text-color)" }} />}
                      label="AVI"
                      sx={{
                        "& .MuiFormControlLabel-label": {
                          fontSize: "14px",
                          color: "var(--text-color)",
                        },
                      }}
                    />
                    <FormControlLabel
                      value="hr"
                      control={<Radio sx={{ color: "var(--text-color)" }} />}
                      label="HR"
                      sx={{
                        "& .MuiFormControlLabel-label": {
                          fontSize: "14px",
                          color: "var(--text-color)",
                        },
                      }}
                    />
                  </RadioGroup>
                </FormControl>
              </Collapse>

              <IconButton onClick={() => setIsAIOpen(!isAIOpen)}>
                {isAIOpen ? (
                  <ArrowRightIcon sx={{ color: "var(--text-color)" }} />
                ) : (
                  <ArrowLeftIcon sx={{ color: "var(--text-color)" }} />
                )}
              </IconButton>
            </Box>
          )}
        </Box>
        <Tooltip
          title={
            <div>
              {siteData && siteData.statistics.avi && (
                <div>
                  AI 미검 이미지 수:{" "}
                  {aggregatedData.ai_missing_s > 0
                    ? formatNumberWithComma(aggregatedData.ai_missing_s)
                    : 0}
                  장
                </div>
              )}
              <div>
                AI 과검 이미지 수:{" "}
                {aggregatedData.ai_over_s > 0
                  ? formatNumberWithComma(aggregatedData.ai_over_s)
                  : 0}
                장
              </div>
              <div>
                이미지 수:{" "}
                {aggregatedData.totalCount > 0
                  ? formatNumberWithComma(aggregatedData.totalCount)
                  : 0}
                장
              </div>
            </div>
          }
          placement="top"
          arrow
          componentsProps={{
            tooltip: {
              sx: {
                fontSize: "1rem",
                padding: "8px 12px",
              },
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "baseline",
              gap: 1,
              justifyContent: "space-between",
            }}
          >
            {siteData && siteData.statistics.avi && (
              <Box sx={{ width: "100%", textAlign: "center" }}>
                <Typography
                  sx={{
                    fontWeight: "bold",
                    color: "var(--stat-missing-text-color)",
                    fontSize: "2rem",
                  }}
                >
                  {aggregatedData.ai_missing_s > 0
                    ? formatNumber(
                        (aggregatedData.ai_missing_s /
                          aggregatedData.ai_missing_m) *
                          100
                      )
                    : 0}
                  %
                </Typography>
                <Typography
                  variant="caption"
                  color="var(--text-color)"
                  sx={{ fontSize: "15px" }}
                >
                  미검률
                </Typography>
              </Box>
            )}

            <Box sx={{ width: "100%", textAlign: "center" }}>
              <Typography
                sx={{
                  fontWeight: "bold",
                  color: "var(--stat-over-text-color)",
                  fontSize: "2rem",
                }}
              >
                {aggregatedData.ai_over_s > 0
                  ? formatNumber(
                      (aggregatedData.ai_over_s / aggregatedData.ai_over_m) *
                        100
                    )
                  : 0}
                %
              </Typography>
              <Typography
                variant="caption"
                color="var(--text-color)"
                sx={{ fontSize: "15px" }}
              >
                과검률
              </Typography>
            </Box>
          </Box>
        </Tooltip>
      </Paper>

      {siteData && siteData.statistics.avi && (
        <Paper
          sx={{
            p: 2,
            backgroundColor: "var(--paper-bg-color)",
            height: "150px",
            minWidth: "300px",
            width: "100%",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              height: "42px",
            }}
          >
            <Typography
              variant="subtitle1"
              color="text.secondary"
              gutterBottom
              sx={{
                fontWeight: "bold",
                width: isAVIOpen ? "" : "100%",
                textAlign: trueCount <= 2 ? "center" : "",
                fontSize: "1.2rem",
                color: "var(--text-color)",
              }}
            >
              AVI
            </Typography>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                height: "42px",
              }}
            >
              <Collapse in={isAVIOpen} orientation="horizontal">
                <FormControl>
                  <RadioGroup
                    row
                    value={referenceAVI}
                    onChange={(e) => setReferenceAVI(e.target.value)}
                  >
                    <FormControlLabel
                      value="ai"
                      control={<Radio sx={{ color: "var(--text-color)" }} />}
                      label="AI"
                      sx={{
                        "& .MuiFormControlLabel-label": {
                          fontSize: "14px",
                          color: "var(--text-color)",
                        },
                      }}
                    />
                    <FormControlLabel
                      value="hr"
                      control={<Radio sx={{ color: "var(--text-color)" }} />}
                      label="HR"
                      sx={{
                        "& .MuiFormControlLabel-label": {
                          fontSize: "14px",
                          color: "var(--text-color)",
                        },
                      }}
                    />
                  </RadioGroup>
                </FormControl>
              </Collapse>

              <IconButton onClick={() => setIsAVIOpen(!isAVIOpen)}>
                {isAVIOpen ? (
                  <ArrowRightIcon sx={{ color: "var(--text-color)" }} />
                ) : (
                  <ArrowLeftIcon sx={{ color: "var(--text-color)" }} />
                )}
              </IconButton>
            </Box>
          </Box>
          <Tooltip
            title={
              <div>
                <div>
                  AVI 미검 이미지 수:{" "}
                  {aggregatedData.avi_missing_s > 0
                    ? formatNumberWithComma(aggregatedData.avi_missing_s)
                    : "?"}
                  장
                </div>
                <div>
                  AVI 과검 이미지 수:{" "}
                  {aggregatedData.avi_over_s > 0
                    ? formatNumberWithComma(aggregatedData.avi_over_s)
                    : 0}
                  장
                </div>
                이미지 수:{" "}
                {aggregatedData.totalCount > 0
                  ? formatNumberWithComma(aggregatedData.totalCount)
                  : 0}
                장
              </div>
            }
            placement="top"
            arrow
            componentsProps={{
              tooltip: {
                sx: {
                  fontSize: "1rem",
                  padding: "8px 12px",
                },
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "baseline",
                gap: 1,
              }}
            >
              <Box sx={{ width: "100%", textAlign: "center" }}>
                <Typography
                  sx={{
                    fontWeight: "bold",
                    color: "var(--stat-missing-text-color)",
                    fontSize: "2rem",
                  }}
                >
                  {aggregatedData.avi_missing_s > 0
                    ? formatNumber(
                        (aggregatedData.avi_missing_s /
                          aggregatedData.avi_missing_m) *
                          100
                      )
                    : "?"}
                  %
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: "15px", color: "var(--text-color)" }}
                >
                  미검률
                </Typography>
              </Box>
              <Box sx={{ width: "100%", textAlign: "center" }}>
                <Typography
                  sx={{
                    fontWeight: "bold",
                    color: "var(--stat-over-text-color)",
                    fontSize: "2rem",
                  }}
                >
                  {aggregatedData.avi_over_s > 0
                    ? formatNumber(
                        (aggregatedData.avi_over_s /
                          aggregatedData.avi_over_m) *
                          100
                      )
                    : 0}
                  %
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: "15px", color: "var(--text-color)" }}
                >
                  과검률
                </Typography>
              </Box>
            </Box>
          </Tooltip>
        </Paper>
      )}
    </Box>
  );
}
